import { UserLanguage } from '../../../../features/account/types/User'
import { LanguageMap } from '../../../../types/LanguageMap'
import { LanguageMaps } from '../../../api/language'
import { ContentLanguageTranslation } from '../../../types/InternalAnalystComment'
import { MotivationNames, MotivationNamesByLanguage, MotivationNamesChange } from '../types/Motivations'

export const motivationLanguages: UserLanguage[] = ['en', 'ja', 'zh']

export type MotivationLocalizationRow = {
  key: string
  names: ContentLanguageTranslation
}

export type MotivationKeyError = 'required' | 'invalid' | 'taken'

export type MotivationLocalizationFormValues = {
  key: string
  names: ContentLanguageTranslation
}

export const motivationsOf = (languageMap: LanguageMap | undefined): MotivationNames => languageMap?.motivations ?? {}

const compareKeys = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)

export const buildMotivationLocalizationRows = (languageMaps: LanguageMaps | undefined): MotivationLocalizationRow[] => {
  if (!languageMaps) return []

  return Object.keys(motivationsOf(languageMaps.en))
    .sort(compareKeys)
    .map((key) => ({
      key,
      names: Object.fromEntries(motivationLanguages.map((language) => [language, motivationsOf(languageMaps[language])[key]?.name ?? ''])),
    }))
}

// Keys are stored as document field names, which can't contain dots or start with `$`.
export const getMotivationKeyError = (key: string, rows: MotivationLocalizationRow[]): MotivationKeyError | undefined => {
  if (!key.trim()) return 'required'
  if (key !== key.trim() || key.includes('.') || key.startsWith('$')) return 'invalid'
  if (rows.some((row) => row.key === key)) return 'taken'
  return undefined
}

export const toMotivationLocalizationFormValues = (row?: MotivationLocalizationRow): MotivationLocalizationFormValues => ({
  key: row?.key ?? '',
  names: { ...(row?.names ?? {}) },
})

export const applyMotivationNamesChange = (languageMaps: LanguageMaps, change: MotivationNamesChange): MotivationNamesByLanguage =>
  Object.fromEntries(
    motivationLanguages.map((language) => {
      const group = motivationsOf(languageMaps[language])
      if (change.type === 'remove') {
        const { [change.key]: _removed, ...rest } = group
        return [language, rest]
      }
      return [language, { ...group, [change.key]: { ...group[change.key], name: change.names[language] ?? '' } }]
    })
  )

const sameNames = (a: MotivationNames | undefined, b: MotivationNames | undefined) => {
  const keys = new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})])
  return [...keys].every((key) => !!a?.[key] && !!b?.[key] && (a[key].name ?? '') === (b[key].name ?? ''))
}

export const getUnstoredMotivationLanguages = (expected: MotivationNamesByLanguage, stored: MotivationNamesByLanguage): UserLanguage[] =>
  motivationLanguages.filter((language) => !sameNames(expected[language], stored[language]))
